import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANG_URL } from '@/constants';
import { LOCALE_TO_HTML_LANG, isAppLocale, localeFromHtmlLang } from '@/constants/appLocale';
import zh from './langs/zh_CN.json';
import zhTW from './langs/zh_TW.json';
import en from './langs/en_US.json';
import ja from './langs/ja_JP.json';
import ko from './langs/ko_KR.json';
import jsCookie from 'js-cookie';
import axios from "axios";
import utils from '@/utils'
// 从 cookie 或 localStorage 获取保存的语言设置
const getSavedLanguage = () => {
  // URL 参数优先级最高，便于通过分享链接直接打开指定语言页面。
  // 例如：/?lang=zh-CN、/?lang=ja-JP、/?lang=en-US
  const urlLanguage = localeFromHtmlLang(
    new URLSearchParams(window.location.search).get('lang'),
  );
  if (urlLanguage) {
    // 内部导航通常不会保留查询参数，因此同时保存 URL 指定的语言。
    jsCookie.set('i18next', urlLanguage);
    localStorage.setItem('i18next', urlLanguage);
    return urlLanguage;
  }

  const savedLang = jsCookie.get('i18next') || localStorage.getItem('i18next');
  if (savedLang && isAppLocale(savedLang)) {
    return savedLang;
  }
  // 默认使用浏览器语言
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('ja')) return 'ja_JP';
  if (browserLang.startsWith('ko')) return 'ko_KR';
  if (browserLang.startsWith('en')) return 'en_US';
  if (browserLang === 'zh-tw' || browserLang === 'zh-hk' || browserLang === 'zh-mo') return 'zh_TW';
  if (browserLang.startsWith('zh')) return 'zh_CN';
  return 'zh_CN'; // 默认中文
};

const currentLanguage = getSavedLanguage();
document.documentElement.setAttribute('lang', LOCALE_TO_HTML_LANG[currentLanguage]);


const localCache = {
  'zh_CN': utils.safeJSONParse(localStorage.getItem('i18next_zh_CN'), {}),
  'zh_TW': utils.safeJSONParse(localStorage.getItem('i18next_zh_TW'), {}),
  'en_US': utils.safeJSONParse(localStorage.getItem('i18next_en_US'), {}),
  'ja_JP': utils.safeJSONParse(localStorage.getItem('i18next_ja_JP'), {}),
  'ko_KR': utils.safeJSONParse(localStorage.getItem('i18next_ko_KR'), {}),
}

const newZh = Object.assign({}, zh, localCache.zh_CN)
const newZhTW = Object.assign({}, zhTW, localCache.zh_TW)
const newEn = Object.assign({}, en, localCache.en_US)
const newJa = Object.assign({}, ja, localCache.ja_JP)
const newKo = Object.assign({}, ko, localCache.ko_KR)

localStorage.setItem('i18next_zh_CN', JSON.stringify(newZh))
localStorage.setItem('i18next_zh_TW', JSON.stringify(newZhTW))
localStorage.setItem('i18next_en_US', JSON.stringify(newEn))
localStorage.setItem('i18next_ja_JP', JSON.stringify(newJa))
localStorage.setItem('i18next_ko_KR', JSON.stringify(newKo))

// 本地资源作为 fallback
const localResources = {
  'zh_CN': {
    translation: newZh,
  },
  'zh_TW': {
    translation: newZhTW,
  },
  'en_US': {
    translation: newEn,
  },
  'ja_JP': {
    translation: newJa,
  },
  'ko_KR': {
    translation: newKo,
  },
};

/**
 * i18next 需要的资源格式
 */
type I18nResources = Record<string, { translation: Record<string, string> }>;


const loadServerResources = async (): Promise<I18nResources | null> => {
  try {    
    const lang = i18n.language || currentLanguage;
    const response = await axios.get(`${LANG_URL}${lang}.json?t=${new Date().getTime()}`) as any;
    if (!response.data || typeof response.data !== 'object' || !response.data.messages) {
      return null;
    }
    const serverResources: I18nResources = {
      [lang]: { translation: response.data.messages }
    };

    localStorage.setItem(`i18next_${lang}`, JSON.stringify(response.data.messages))

    return serverResources;
   
  } catch (error) {
    console.warn('Failed to load i18n resources from server:', error);
    return null;
  }
};



// 初始化 i18n（先使用本地资源）
i18n
  // 将 i18n 实例传递给 react-i18next
  .use(initReactI18next)
  // 初始化 i18next
  .init({
    resources: localResources,
    // 默认语言
    fallbackLng: 'zh_CN',
    // 当前语言
    lng: currentLanguage,
    // 调试模式
    debug: false,
    // 插值配置
    interpolation: {
      escapeValue: false,
    },
  });

/**
 * 将服务端资源添加到 i18n
 * @param serverResources 服务端返回的资源对象
 */
const addServerResourcesToI18n = (serverResources: I18nResources) => {
  Object.keys(serverResources).forEach((lang) => {
    i18n.addResourceBundle(
      lang,
      'translation',
      serverResources[lang].translation,
      true, // deep: 深度合并，而不是替换整个对象
      true  // overwrite: 覆盖已存在的键值
    );
  });
  

  const currentLang = i18n.language;

  i18n.reloadResources(currentLang).then(() => {
    i18n.changeLanguage(currentLang);
  });
};

// 异步加载服务端资源并合并到 i18n
// 应用启动时自动加载，如果服务端资源加载失败，会使用本地资源作为 fallback
loadServerResources()
  .then((serverResources) => {
    if (serverResources) {
      addServerResourcesToI18n(serverResources);
    }
  })
  .catch((error) => {
    console.warn('Failed to load server i18n resources, using local resources:', error);
  });

// // 监听语言变化，保存到 cookie
i18n.on('languageChanged', (lng) => {
  jsCookie.set('i18next', lng);
  localStorage.setItem('i18next', lng);
  if (isAppLocale(lng)) {
    document.documentElement.setAttribute('lang', LOCALE_TO_HTML_LANG[lng]);
  }
});

/**
 * 重新加载服务端资源
 * 用于手动刷新翻译资源，例如在管理后台更新翻译后
 * 
 * @returns 成功返回 true，失败返回 false
 * 
 * @example
 * ```typescript
 * import { reloadServerResources } from '@/i18n';
 * 
 * // 在需要刷新翻译时调用
 * const success = await reloadServerResources();
 * if (success) {
 *   console.log('翻译资源已更新');
 * }
 * ```
 */
export const reloadServerResources = async (): Promise<boolean> => {
  try {
    const serverResources = await loadServerResources();
    if (serverResources) {
      addServerResourcesToI18n(serverResources);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to reload server i18n resources:', error);
    return false;
  }
};

export default i18n;
